<script setup lang="ts">
import { computed } from 'vue'

export interface SidebarItem {
    key: string
    label: string
    icon?: unknown
    to: string
    visible: boolean
    badge?: number
}

export interface SidebarGroup {
    key: string
    label: string
    items: SidebarItem[]
}

const props = defineProps<{
    groups: SidebarGroup[]
    activeKey?: string
    collapsed: boolean
    // Mobile drawer open state. Only affects rendering at viewports < lg —
    // at lg+ the sidebar is always visible and this prop is ignored visually
    // (translate-x-0 forced via lg:translate-x-0).
    mobileOpen?: boolean
    logoSrc?: string
    appName?: string
}>()

defineEmits<{
    (e: 'navigate', to: string): void
    (e: 'close-mobile'): void
}>()

// Hide groups whose items are all gated out, plus collapse separator visibility.
const visibleGroups = computed(() =>
    props.groups
        .map(g => ({ ...g, items: g.items.filter(i => i.visible) }))
        .filter(g => g.items.length > 0)
)
</script>

<template>
    <aside
        :class="[
            'sidebar-root',
            'fixed inset-y-0 left-0 z-sidebar bg-ink text-white flex flex-col',
            'transition-[width,transform] duration-normal',
            // Mobile: full drawer width (280px). Desktop (lg+): respect collapsed prop.
            'w-[280px] lg:w-auto',
            collapsed ? 'lg:w-sw-c' : 'lg:w-sw',
            // Mobile transform: hidden off-canvas by default, slides in when mobileOpen.
            // At lg+ always shown (override translate).
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            'lg:translate-x-0'
        ]"
    >
        <div class="py-4 flex flex-col items-center justify-center bg-black/10 border-b border-white/10 overflow-hidden">
            <!--
                Logo source is 512x127. Mascot fills the left 130x127 region.
                Collapsed (desktop only): show mascot only via background-image
                with sized scale so only that left slice is visible.
                Expanded (default + mobile): full logo as <img>, plus app name below.

                On mobile (< lg), the drawer is always 280px wide regardless of
                the `collapsed` prop, so we always render the expanded form there.
                The collapsed-only "icons sliver" UI is gated to lg+ via classes.
            -->
            <!-- Expanded logo: always rendered, hidden on lg+ when collapsed -->
            <img
                v-if="logoSrc"
                :src="logoSrc"
                alt=""
                :class="['h-12 max-w-[80%] object-contain', collapsed ? 'lg:hidden' : '']"
            />
            <!-- Collapsed sliver logo: only on lg+ when collapsed -->
            <div
                v-if="logoSrc"
                class="hidden w-9 aspect-[130/127] bg-no-repeat bg-left-top flex-none"
                :class="collapsed ? 'lg:flex' : ''"
                :style="{ backgroundImage: `url(${logoSrc})`, backgroundSize: `${(512 / 130) * 100}% auto` }"
                :aria-label="appName ?? 'Admin'"
                role="img"
            />
            <span
                :class="[
                    'mt-2 font-semibold text-xs tracking-wide',
                    collapsed ? 'lg:hidden' : ''
                ]"
            >{{ appName ?? 'Admin' }}</span>
        </div>
        <nav class="flex-1 py-2.5 px-2 overflow-y-auto flex flex-col gap-px">
            <div v-for="(group, gi) in visibleGroups" :key="group.key">
                <!-- Group label — uppercase, dim. Always rendered; hidden on lg+ when collapsed.
                     Top padding creates the visual gap between sections without
                     needing a hard separator line. -->
                <div
                    :class="[
                        'text-[12px] font-bold uppercase tracking-[.8px] text-white/40 px-2.5 pt-[18px] pb-1.5',
                        collapsed ? 'lg:hidden' : ''
                    ]"
                >
                    {{ group.label }}
                </div>
                <!-- Collapsed divider: only on lg+ when collapsed (skip before first group). -->
                <hr
                    v-if="gi > 0"
                    :class="[
                        'my-2 mx-2 border-0 border-t border-white/10',
                        collapsed ? 'hidden lg:block' : 'hidden'
                    ]"
                    :aria-label="group.label"
                />
                <ul class="flex flex-col gap-px">
                    <li v-for="item in group.items" :key="item.key">
                        <button
                            type="button"
                            :title="collapsed ? item.label : undefined"
                            :class="[
                                'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-6 text-sm transition-colors duration-fast',
                                activeKey === item.key
                                    ? 'bg-white/10 text-white font-semibold border-l-[3px] border-orange'
                                    : 'text-white/70 hover:bg-white/[.08] hover:text-white'
                            ]"
                            @click="$emit('navigate', item.to); $emit('close-mobile')"
                        >
                            <component
                                :is="item.icon"
                                v-if="item.icon"
                                class="w-4 h-4 flex-none"
                                :class="activeKey === item.key ? 'text-orange opacity-100' : 'opacity-55'"
                            />
                            <span :class="['flex-1 text-left', collapsed ? 'lg:hidden' : '']">{{ item.label }}</span>
                            <span
                                v-if="item.badge && item.badge > 0"
                                :class="['text-xs px-1.5 py-0.5 rounded bg-amber-500 text-white leading-none', collapsed ? 'lg:hidden' : '']"
                            >{{ item.badge }}</span>
                        </button>
                    </li>
                </ul>
            </div>
        </nav>
    </aside>
</template>

<style>
/*
 * Dark-mode override: in dark mode the global `--color-ink` token flips
 * to a light shade (semantically it represents primary text). The
 * sidebar must remain a dark surface so the lighter page background
 * has contrast — pin its background to a fixed dark color
 * independent of the theme token. `text-white` keeps `--color-white`,
 * which we do NOT override, so child text stays white.
 *
 * Unscoped on purpose: Vue scoped style + `:global()` + child selector
 * does not reliably combine into a single rule (the descendant gets
 * dropped). A plain global rule is safer here.
 */
[data-theme='dark'] .sidebar-root {
    background-color: #0a1f1c;
}
</style>
